import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LINKS, NAV_ITEMS, scrollToId } from "../data/content";
import { MonogramMark, WhatsAppIcon } from "./icons";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="press-kit-header-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled
          ? "bg-[#F8FAF8]/85 backdrop-blur-xl border-[#0D3B3E]/10 shadow-[0_8px_30px_rgba(13,59,62,0.06)]"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <button
          data-testid="header-logo-monogram"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 shrink-0"
        >
          <MonogramMark className="w-9 h-9" />
          <span className="hidden sm:block text-left">
            <span className="block font-display font-bold text-[#0D3B3E] leading-none text-base">Dr. Mugdha Adhikari</span>
            <span className="block text-[10px] font-mono tracking-[0.2em] text-[#0D9488] mt-1">PRESS KIT // 2026</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-link-${item.id}`}
              onClick={() => go(item.id)}
              className="text-[11px] font-mono tracking-[0.18em] text-[#0D3B3E]/70 hover:text-[#0D9488] transition-colors duration-300 relative group"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#0D9488] transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <span
            data-testid="header-status-indicator"
            className="hidden md:flex items-center gap-2 text-[10px] font-mono tracking-[0.18em] text-[#0D3B3E]/70 border border-[#0D3B3E]/15 rounded-full px-3 py-1.5"
          >
            <span className="status-dot w-1.5 h-1.5 rounded-full bg-[#0D9488]" />
            ACCEPTING PATIENTS
          </span>
          <a
            data-testid="header-whatsapp-cta"
            href={LINKS.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center gap-2 bg-[#0D3B3E] text-[#F8FAF8] text-[11px] font-mono tracking-[0.15em] rounded-full px-4 py-2.5 hover:bg-[#0D9488] transition-colors duration-300"
          >
            <WhatsAppIcon className="w-4 h-4" />
            WHATSAPP
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-[#0D3B3E] p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#F8FAF8]/95 backdrop-blur-xl border-t border-[#0D3B3E]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-link-${item.id}`}
                  onClick={() => go(item.id)}
                  className="text-left text-sm font-mono tracking-[0.2em] text-[#0D3B3E] py-1"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
